import { useEffect, useState } from "react";
import {
  belongsTo,
  dismissFlushNotes,
  type FlushOutcome,
  flushGeneration,
  flushNotesFor,
  listQueued,
  onFlushed,
  onFlushNotesChange,
  onQueueChange,
  type QueuedPunch,
  startAutoFlush,
} from "../lib/offlineQueue";
import { syncPushSubscription } from "../lib/push";
import { useSession } from "../session";

export interface PunchQueue {
  /** The signed-in person's punches saved on this phone and not yet sent. */
  mine: QueuedPunch[];
  /** Punches saved by someone else who used this phone: kept, never sent as this person. */
  others: QueuedPunch[];
}

/** Punches saved on this phone and not yet sent. Also starts the automatic sender. */
export function usePunchQueue(): PunchQueue {
  const { me } = useSession();
  const userId = me?.user.id ?? null;
  const [items, setItems] = useState<QueuedPunch[]>([]);
  useEffect(() => {
    startAutoFlush();
    let alive = true;
    const load = () => {
      listQueued()
        .then((i) => alive && setItems(i))
        .catch(() => {});
    };
    load();
    const off = onQueueChange(load);
    return () => {
      alive = false;
      off();
    };
  }, []);
  return {
    mine: items.filter((i) => belongsTo(i, userId)),
    others: items.filter((i) => !belongsTo(i, userId)),
  };
}

/** Results of saved punches that were sent (possibly while another screen was open). */
export function useFlushNotes(): { notes: FlushOutcome[]; dismiss: (clientIds?: string[]) => void } {
  const { me } = useSession();
  const userId = me?.user.id ?? null;
  const [notes, setNotes] = useState<FlushOutcome[]>(() => (userId ? flushNotesFor(userId) : []));
  useEffect(() => {
    if (!userId) {
      setNotes([]);
      return;
    }
    const load = () => setNotes(flushNotesFor(userId));
    load();
    return onFlushNotesChange(load);
  }, [userId]);
  return {
    notes,
    dismiss: (clientIds) => {
      if (userId) dismissFlushNotes(userId, clientIds);
    },
  };
}

/** Poll /me this often while a punch is waiting for the office's approval. */
const PENDING_POLL_MS = 30_000;
/** Don't refetch /me on every quick app switch. */
const MIN_VISIBLE_RELOAD_MS = 10_000;

let seenFlushGeneration = 0;
let pushSyncedFor: string | null = null;

/**
 * Keeps the signed-in session (clocked in or not, waiting for approval) up to date:
 * - after saved punches are sent, even if that happened before this screen mounted;
 * - when the app comes back to the front;
 * - every 30 s while a punch is waiting for approval and the app is in front.
 * Also, once per sign-in, points this phone's notifications at the person signed in.
 */
export function useSessionRefresh(): void {
  const { me, reload } = useSession();
  const userId = me?.user.id ?? null;
  const pending = me?.openVisit?.in.result === "pending" || me?.openVisit?.out?.result === "pending";

  useEffect(() => {
    if (!userId || pushSyncedFor === userId) return;
    pushSyncedFor = userId;
    void syncPushSubscription();
  }, [userId]);

  useEffect(() => {
    const catchUp = () => {
      if (flushGeneration() === seenFlushGeneration) return;
      seenFlushGeneration = flushGeneration();
      void reload();
    };
    catchUp();
    return onFlushed(catchUp);
  }, [reload]);

  useEffect(() => {
    let last = Date.now();
    const refresh = (minGap: number) => {
      if (document.visibilityState !== "visible" || Date.now() - last < minGap) return;
      last = Date.now();
      void reload();
    };
    const onVisible = () => refresh(MIN_VISIBLE_RELOAD_MS);
    document.addEventListener("visibilitychange", onVisible);
    const timer = pending ? setInterval(() => refresh(PENDING_POLL_MS / 2), PENDING_POLL_MS) : null;
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      if (timer) clearInterval(timer);
    };
  }, [pending, reload]);
}

/** Current time, refreshed every `intervalMs`. */
export function useNow(intervalMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    const onVisible = () => document.visibilityState === "visible" && setNow(Date.now());
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [intervalMs]);
  return now;
}

/** True while the browser thinks it's online. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}
